import { useMemo, useState } from 'react'

import { Application } from '@builddocs/filestore-api'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import {
  Box,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import isValid from 'date-fns/esm/isValid/index'
import formatISO from 'date-fns/formatISO'
import isMatch from 'date-fns/isMatch'
import parseISO from 'date-fns/parseISO'

export const getNameField = (field: string, documentType?: Application) => {
  switch (field) {
    case 'name':
    case 'documentName':
      return 'Наименование'
    case 'number':
    case 'documentNumber':
      return 'Номер'
    case 'sheet':
      return 'Лист'
    case 'issueDate':
      return 'Дата выдачи'
    case 'expirationDate':
      return 'Дата окончания'
    case 'cipher':
      if (documentType === Application.EXECUTIVE_SCHEME) {
        return 'Номер документа'
      }
      return 'Шифр'
    case 'place':
      return 'Место проведения работ'
    case 'description':
      return 'Описание'
    case 'workDate':
      return 'Дата начала работ'
    case 'workDateEnd':
      return 'Дата окончания работ'
    case 'organization':
      return 'Организация'
    case 'date':
      return 'Дата документа'
    default:
      return field
  }
}
const getEditField = ({
  newValue,
  setNewValue,
  id,
  onSave,
  field,
  disabled,
  options,
  label,
  documentType,
  modificationAllowed,
}: {
  newValue: string | Date | undefined
  setNewValue: (value: string | Date) => void
  id: string
  onSave: (value: string) => void
  field?: string
  disabled?: boolean
  options?: string[]
  label?: string
  documentType?: Application
  modificationAllowed?: boolean
}) => {
  switch (field) {
    case 'workDate':
    case 'workDateEnd':
    case 'issueDate':
    case 'expirationDate':
    case 'date': {
      const maxDate =
        field === 'expirationDate' || field === 'workDateEnd'
          ? null
          : new Date()

      return (
        <DatePicker
          clearable
          disabled={disabled}
          label={getNameField(field)}
          mask="__.__.____"
          maxDate={maxDate}
          renderInput={params => (
            <TextField disabled={disabled} size="small" {...params} />
          )}
          value={
            isMatch(newValue as string, 'd MMMM yyyy г.')
              ? parseISO(newValue as string)
              : newValue
              ? (newValue as string)
              : null
          }
          onChange={newValue => newValue && setNewValue(newValue)}
        />
      )
    }

    case 'sheet':
      return (
        <TextField
          key={id}
          fullWidth
          disabled={
            !(
              !disabled ||
              (documentType === Application.EXECUTIVE_DRAWING &&
                modificationAllowed !== false)
            )
          }
          id={id}
          label={getNameField(field)}
          size="small"
          value={newValue ?? ''}
          onChange={e => setNewValue(e.target.value)}
        />
      )

    case 'input':
    case 'name':
    case 'documentName':
    case 'number':
    case 'documentNumber':
    case 'cipher':
    case 'place':
    case 'description':
    case 'organization':
      return (
        <TextField
          key={id}
          fullWidth
          disabled={disabled}
          id={id}
          label={getNameField(field, documentType)}
          size="small"
          value={newValue ?? ''}
          onChange={e => setNewValue(e.target.value)}
        />
      )

    case 'select': {
      if (typeof newValue === 'string' || typeof newValue === 'undefined') {
        return (
          <Stack spacing={2}>
            <FormControl sx={{ m: 1, minWidth: 120 }}>
              <InputLabel id="demo-controlled-open-select-label">
                Выберите вариант
              </InputLabel>
              <Select
                defaultValue=""
                id="grouped-select"
                label="Выберите вариант"
                value={newValue}
                onChange={e => {
                  setNewValue(e.target.value)
                  onSave(e.target.value)
                }}
              >
                {options?.map(item => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )
      }
      return null
    }

    default: {
      if (typeof newValue === 'string' || typeof newValue === 'undefined') {
        return (
          <TextField
            key={id}
            multiline
            id={id}
            label={label ?? ''}
            maxRows={5}
            sx={{ width: 512 }}
            value={newValue ?? ''}
            onChange={e => setNewValue(e.target.value)}
          />
        )
      }
      return null
    }
  }
}

interface OwnProps {
  id: string
  value: string
  onSave: (value: string) => void
  field?: string
  disabled?: boolean
  options?: string[]
  label?: string
  documentType?: Application
  modificationAllowed?: boolean
}

const EditTextField = (props: OwnProps) => {
  const {
    field,
    value,
    id,
    disabled,
    onSave,
    options,
    label,
    documentType,
    modificationAllowed,
  } = props
  const [newValue, setNewValue] = useState<string | Date>(value)
  const [initialValue] = useState<string>(value)
  const isValidDate = useMemo(() => {
    if (field === 'expirationDate') {
      return newValue instanceof Date && isValid(newValue)
        ? newValue > new Date('01.01.2010')
        : false
    } else {
      return newValue instanceof Date && isValid(newValue)
        ? newValue <= new Date() && newValue > new Date('01.01.2010')
        : false
    }
  }, [newValue])

  const isDisabled = useMemo(
    () =>
      newValue instanceof Date
        ? !isValid(newValue) ||
          !isValidDate ||
          (isValid(newValue) &&
            formatISO(new Date(newValue), { representation: 'date' }) === value)
        : newValue === value,
    [newValue, value, isValid],
  )

  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Box flexGrow={1}>
        {getEditField({
          newValue,
          setNewValue,
          id,
          onSave,
          field,
          disabled,
          options,
          label,
          documentType,
          modificationAllowed,
        })}
      </Box>
      {(initialValue !== newValue ||
        (initialValue === newValue && initialValue !== value)) && (
        <Stack alignItems="center" direction="row" spacing={1}>
          <IconButton
            size="small"
            onClick={() => {
              onSave(initialValue)
              setNewValue(initialValue)
            }}
          >
            <ClearRoundedIcon />
          </IconButton>
          <IconButton
            disabled={isDisabled}
            size="small"
            onClick={() =>
              onSave(
                newValue instanceof Date
                  ? formatISO(new Date(newValue), { representation: 'date' })
                  : newValue,
              )
            }
          >
            <CheckRoundedIcon />
          </IconButton>
        </Stack>
      )}
    </Stack>
  )
}

export default EditTextField
